import React from 'react'
import { useSelector } from 'react-redux'
import { useDashboardReview } from '../../hooks/useDashboardReview'
import styles from '../DashboardReview/styles.module.css'
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, FileText, ChevronDown } from 'lucide-react'
import { Button, DropdownMenu } from 'components/ui'
import { isRtlSelector } from 'redux/selectors/general'
import { useExecutionText } from '../../hooks/useExecutionText'
import { useTaskOverviewContext } from '../../TaskOverviewStackProvider'
import { ExecutionStatus } from '../ExecutionStatus'
import { IMediaSubtaskPager } from '../../useMediaSubtaskPager'

type Props = Pick<IMediaSubtaskPager, 'position' | 'total' | 'isBusy' | 'hasPrev' | 'hasNext' | 'goPrev' | 'goNext'> & {
  onSelect?: (id: string) => void
}

export function ExecutionSubtaskNavigation({
  position,
  total,
  isBusy,
  hasPrev,
  hasNext,
  goPrev,
  goNext,
  onSelect,
}: Props) {
  const review = useDashboardReview()
  const { navigateToSubtask } = useTaskOverviewContext()
  const rtl = useSelector(isRtlSelector)
  const t = useExecutionText()

  if (review)
    return (
      <div className={`${styles.navigation} ${styles.subtaskNavigation}`}>
        <DropdownMenu
          triggerVariant="ghost"
          triggerButtonProps={{
            disabled: isBusy,
            'aria-label': t.subtask,
            h: '30px',
            px: '6px',
            flex: 1,
            minW: 0,
            _hover: { bg: 'transparent', color: '#0F172A' },
            _active: { bg: 'transparent' },
          }}
          triggerContent={
            <>
              <FileText size={15} />
              <span
                className={styles.contextName}
                title={review.subtasks.find((item) => item.st_id === review.selectedId)?.title}
              >
                {review.subtasks.find((item) => item.st_id === review.selectedId)?.title || t.subtask}
              </span>
              <ChevronDown size={14} />
            </>
          }
          isDisabled={isBusy}
          selectedId={review.selectedId || undefined}
          selectionIndicator="radio"
          placement={rtl ? 'bottom-start' : 'bottom-end'}
          menuWidth="min(340px, calc(100vw - 32px))"
          menuClassName={styles.navigationMenu}
          options={review.subtasks.map((subtask, index) => ({
            id: subtask.st_id,
            value: subtask,
            disabled: isBusy,
            sectionTitle: index === 0 ? <strong className={styles.menuTitle}>{t.subtask}</strong> : undefined,
            label: (
              <span className={styles.contextRow}>
                <span>{subtask.title}</span>
                <ExecutionStatus status={review.statusFor(review.task.dashboardContextId || '', subtask.st_id)} />
              </span>
            ),
          }))}
          onSelect={({ value }) => {
            if (isBusy) return
            if (onSelect) onSelect(value.st_id)
            else navigateToSubtask(value)
          }}
        />
        <div className={styles.pager}>
          <Button
            variant="icon"
            _hover={{ bg: 'transparent', color: '#0F172A' }}
            _active={{ bg: 'transparent' }}
            isDisabled={isBusy || !hasPrev}
            onClick={goPrev}
            aria-label={t.previousSubtask}
          >
            {rtl ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
          </Button>
          <bdi>
            {position} / {total}
          </bdi>
          <Button
            variant="icon"
            _hover={{ bg: 'transparent', color: '#0F172A' }}
            _active={{ bg: 'transparent' }}
            isDisabled={isBusy || !hasNext}
            onClick={goNext}
            aria-label={t.nextSubtask}
          >
            {rtl ? <ChevronLeft size={15} /> : <ChevronRight size={15} />}
          </Button>
        </div>
      </div>
    )

  return (
    <div className="execution-subtask-nav">
      <Button variant="secondary" isDisabled={isBusy || !hasPrev} onClick={goPrev} aria-label={t.previousSubtask}>
        {rtl ? <ArrowRight size={16} /> : <ArrowLeft size={16} />}
      </Button>
      <span>
        {t.subtask}{' '}
        <bdi>
          {position} / {total}
        </bdi>
      </span>
      <Button variant="secondary" isDisabled={isBusy || !hasNext} onClick={goNext} aria-label={t.nextSubtask}>
        {rtl ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
      </Button>
    </div>
  )
}
